import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Send, Loader2 } from 'lucide-react'
import type { Reply } from '@olimhub/shared'
import { LANGUAGES, POST_CHAR_LIMIT } from '@olimhub/shared'
import type { Language } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'
import { useAuthStore } from '@/store/auth.store'
import { postsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { useMentionAutocomplete } from '@/hooks/useMentionAutocomplete'
import { MentionDropdown } from './MentionDropdown'

interface ReplyComposerProps {
  postId: string
  parentUsername: string
  defaultLanguage?: Language
  onReplyCreated: (reply: Reply) => void
}

const ACTIVE_LANGUAGES = LANGUAGES.filter((l) => l !== 'he')

export function ReplyComposer({ postId, parentUsername, defaultLanguage, onReplyCreated }: ReplyComposerProps) {
  const { t } = useTranslation()
  const { account, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [content, setContent] = useState('')
  const [language, setLanguage] = useState<Language>(defaultLanguage ?? 'en')
  const [isFocused, setIsFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const mention = useMentionAutocomplete(content, setContent, textareaRef)

  const charsLeft = POST_CHAR_LIMIT - content.length
  const canSubmit = content.trim().length > 0 && charsLeft >= 0 && !isSubmitting

  useEffect(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [content])

  if (!isAuthenticated) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--bg-sunk)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/signup?from=${encodeURIComponent(location.pathname)}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)',
            color: 'var(--brand)', fontWeight: 600, padding: 0,
          }}
        >
          {t('auth.signup_cta')}
        </button>{' '}
        {t('post.signup_to_reply')}
      </div>
    )
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      const reply = await postsApi.createReply(postId, { content: content.trim(), language })
      onReplyCreated(reply)
      setContent('')
      setIsFocused(false)
    } catch {
      toast.error('Could not post reply. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <Avatar
          name={account!.displayName || account!.username}
          avatarUrl={account!.avatarUrl}
          animalAvatar={account!.animalAvatar}
          size="sm"
        />
      </div>

      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={content}
          placeholder={t('post.reply_placeholder', { username: parentUsername })}
          rows={isFocused ? 3 : 1}
          onFocus={() => setIsFocused(true)}
          onChange={(e) => {
            const v = e.target.value.slice(0, POST_CHAR_LIMIT + 20)
            setContent(v)
            mention.handleChange(v, e.target.selectionStart)
          }}
          onKeyDown={(e) => {
            mention.handleKeyDown(e)
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !mention.showDropdown) handleSubmit()
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            border: `1.5px solid ${isFocused ? 'var(--brand)' : 'var(--border)'}`,
            background: 'var(--bg-sunk)',
            color: 'var(--text-strong)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            lineHeight: 'var(--lh-normal)',
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box',
            minHeight: 36,
            transition: 'border-color var(--dur-fast)',
            overflow: 'hidden',
          }}
        />
        {mention.showDropdown && (
          <MentionDropdown
            results={mention.results}
            selectedIdx={mention.selectedIdx}
            onSelect={mention.selectMention}
            onClose={mention.closeMention}
            anchorRef={textareaRef}
          />
        )}

        {isFocused && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            {/* Language selector */}
            <div style={{ display: 'flex', gap: 4 }}>
              {ACTIVE_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  style={{
                    padding: '3px 8px',
                    borderRadius: 'var(--radius-pill)',
                    border: `1.5px solid ${language === lang ? 'var(--brand)' : 'var(--border)'}`,
                    background: language === lang ? 'color-mix(in srgb, var(--brand) 10%, transparent)' : 'none',
                    color: language === lang ? 'var(--brand)' : 'var(--text-muted)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                  }}
                >
                  {lang}
                </button>
              ))}
            </div>

            <span
              style={{
                marginInlineStart: 'auto',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-xs)',
                color: charsLeft < 20 ? 'var(--danger)' : 'var(--text-faint)',
                fontWeight: charsLeft < 20 ? 600 : 400,
              }}
            >
              {charsLeft}
            </span>

            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 12px',
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                background: canSubmit ? 'var(--brand)' : 'var(--border)',
                color: canSubmit ? '#fff' : 'var(--text-faint)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'background var(--dur-fast)',
              }}
            >
              {isSubmitting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={13} />}
              {t('post.reply')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
